import { useEffect, useRef, useState } from 'react';
import { Icons } from '../Icons.jsx';
import { speak } from '../../utils/speech.js';
import { startRecognition, recognitionAvailable } from '../../utils/speechRecognition.js';
import { answersMatch, normalize } from '../../utils/normalize.js';
import { tapLight } from '../../utils/haptics.js';
import { markMicDenied } from '../../store/progress.js';
import { BALL } from '../../theme.js';
import { t } from '../../i18n.js';
import { Sentry } from '../../sentry.js';

const T = BALL;

// True if `given` and `expected` share the same multiset of normalized
// tokens — used as a forgiveness pass after `answersMatch` fails on
// word order alone.
function tokensMatchAnyOrder(given, expected) {
  const g = normalize(given).split(/\s+/).filter(Boolean).sort();
  const e = normalize(expected).split(/\s+/).filter(Boolean).sort();
  if (g.length === 0 || g.length !== e.length) return false;
  return g.every((w, i) => w === e[i]);
}

function scoreTranscript(transcript, accept) {
  if (!transcript) return false;
  if (answersMatch(transcript, accept)) return true;
  const expects = Array.isArray(accept) ? accept : [accept];
  return expects.some((e) => tokensMatchAnyOrder(transcript, e));
}

export default function Speak({ exercise, lang, onResult, locked }) {
  const [listening, setListening] = useState(false);
  const [partial, setPartial] = useState('');
  const [status, setStatus] = useState('idle'); // 'idle' | 'listening' | 'denied' | 'unavailable'
  const sessionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    recognitionAvailable().then((ok) => {
      if (cancelled) return;
      if (!ok) setStatus('unavailable');
    });
    return () => { cancelled = true; sessionRef.current?.stop?.(); };
  }, []);

  function finish(transcript) {
    const correct = scoreTranscript(transcript, exercise.accept);
    Sentry.addBreadcrumb({
      category: 'exercise',
      message: 'speak-submit',
      data: { correct, hadTranscript: !!transcript },
    });
    onResult({
      correct,
      given: transcript || '',
      expected: Array.isArray(exercise.accept) ? exercise.accept[0] : exercise.accept,
    });
  }

  function skip() {
    onResult({ correct: true, skipped: true, given: '', expected: exercise.prompt });
  }

  async function toggleMic() {
    if (locked) return;
    if (listening) { sessionRef.current?.stop?.(); return; }
    tapLight();
    setPartial('');
    setStatus('listening');
    setListening(true);
    const session = await startRecognition({
      lang: 'hr-HR',
      onPartial: (text) => setPartial(text),
      onFinal: (text) => {
        setListening(false);
        setStatus('idle');
        sessionRef.current = null;
        finish(text);
      },
      onError: (err) => {
        setListening(false);
        sessionRef.current = null;
        if (err?.code === 'denied') {
          setStatus('denied');
          markMicDenied();
          setTimeout(skip, 1400);
          return;
        }
        if (err?.code === 'unavailable' || err?.code === 'not-allowed') {
          setStatus('unavailable');
          setTimeout(skip, 1400);
          return;
        }
        setStatus('idle');
      },
    });
    sessionRef.current = session;
  }

  const micActive = listening;
  const micBg = micActive ? T.red : T.primary;
  const micDeep = micActive ? T.redDeep : T.primaryDeep;
  const statusLabel = (() => {
    if (status === 'listening') return t('speakListening', lang);
    if (status === 'denied') return t('speakDenied', lang);
    if (status === 'unavailable') return t('speakNoMic', lang);
    return t('speakTap', lang);
  })();

  return (
    <div style={{ padding: '4px 22px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{
          fontSize: 11, fontWeight: 800, color: T.primary, letterSpacing: '0.12em',
        }}>{t('speakHeader', lang)}</div>
        <h2 style={{
          fontSize: 24, fontWeight: 900, color: T.ink, margin: '6px 0 0', lineHeight: 1.15,
        }}>{exercise.translation}</h2>
      </div>

      <button
        type="button"
        onClick={() => speak(exercise.speakText)}
        style={{
          appearance: 'none', cursor: 'pointer',
          background: T.bgAlt, border: `2px solid ${T.hairline}`,
          borderRadius: 16, padding: '14px 16px',
          boxShadow: `0 3px 0 ${T.hairline}`,
          display: 'flex', alignItems: 'center', gap: 14,
          textAlign: 'left', fontFamily: 'inherit', width: '100%',
        }}
      >
        <div style={{
          width: 44, height: 44, borderRadius: '50%',
          background: T.blue, color: '#fff', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 3px 0 ${T.blueDeep}`,
        }}>
          {Icons.speaker('#fff')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.primary }}>{exercise.prompt}</div>
        </div>
      </button>

      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
        padding: '12px 0',
      }}>
        <div style={{ position: 'relative' }}>
          {micActive && (
            <div style={{
              position: 'absolute', inset: -12, borderRadius: '50%',
              border: `3px solid ${T.red}`,
              animation: 'pulse 1.4s ease-out infinite',
              pointerEvents: 'none',
            }} />
          )}
          <button
            type="button"
            onClick={toggleMic}
            disabled={locked || status === 'denied' || status === 'unavailable'}
            style={{
              appearance: 'none', border: 'none',
              cursor: locked || status === 'denied' || status === 'unavailable' ? 'not-allowed' : 'pointer',
              width: 110, height: 110, borderRadius: '50%',
              background: micBg, boxShadow: `0 6px 0 ${micDeep}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: status === 'denied' || status === 'unavailable' ? 0.5 : 1,
              transition: 'background 120ms, box-shadow 120ms',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 44 }}>🎤</span>
          </button>
        </div>
        <div style={{
          fontSize: 12, fontWeight: 800,
          color: status === 'denied' || status === 'unavailable' ? T.redDeep : T.primary,
          letterSpacing: '0.06em', textAlign: 'center', maxWidth: 320,
        }}>{statusLabel}</div>

        {partial && (
          <div style={{
            background: T.bgAlt, border: `2px dashed ${T.hairline}`,
            borderRadius: 12, padding: '10px 14px',
            color: T.inkSoft, fontStyle: 'italic', fontWeight: 600,
            maxWidth: 320, textAlign: 'center',
          }}>
            {partial}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={skip}
        disabled={locked}
        style={{
          appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
          color: T.inkSoft, fontWeight: 800, fontSize: 13, letterSpacing: '0.04em',
          textTransform: 'uppercase', textDecoration: 'underline', padding: 8,
          alignSelf: 'center',
        }}
      >
        {t('speakSkip', lang)}
      </button>
    </div>
  );
}
